import React, { useEffect, useState } from "react";
import axios from "axios";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasNode, CanvasNodeData } from "../../models/CanvasNode.ts";
import VariablePicker from "../Common/VariablePicker.tsx";
import AttributePicker from "../Common/AttributePicker.tsx";
import { Environment as environment } from "../../environment.ts";
import { Type, FileText, ChevronDown, Info, Image as ImageIcon, Plus, Trash2, Settings2, Database } from 'lucide-react';

interface Attribute {
    attributeId: string;
    attributeName: string;
    attributeType: 'TEXT' | 'NUMBER' | 'ARRAY' | 'OBJECT' | 'BOOLEAN';
}

interface StaticCard {
    id: string;
    imageUrl: string;
    text: string;
    buttons: { id: string; text: string }[];
}

const CarouselDialog: React.FC<any> = ({ data, onClose }) => {
    const { nodes, setNodes, businessId } = useFlowStore();

    const [attributes, setAttributes] = useState<Attribute[]>([]);
    const [mode, setMode] = useState<"dynamic" | "static">("dynamic");

    // Dynamic state
    const [arrayAttribute, setArrayAttribute] = useState("");
    const [titlePath, setTitlePath] = useState("");
    const [descPath, setDescPath] = useState("");
    const [imagePath, setImagePath] = useState("");
    const [dynamicButtons, setDynamicButtons] = useState<{ id: string; text: string }[]>([]);

    // Static state
    const [staticCards, setStaticCards] = useState<StaticCard[]>([]);

    // Common state
    const [body, setBody] = useState("");
    const [saveAs, setSaveAs] = useState("");
    const [sampleJson, setSampleJson] = useState("");
    const [inferredPaths, setInferredPaths] = useState<string[]>([]);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

    const flattenObject = (obj: any, prefix = ''): string[] => {
        let paths: string[] = [];
        if (typeof obj === 'object' && obj !== null) {
            for (const key in obj) {
                const newPrefix = prefix ? `${prefix}.${key}` : key;
                paths.push(newPrefix);
                if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                    paths = paths.concat(flattenObject(obj[key], newPrefix));
                }
            }
        }
        return paths;
    };

    useEffect(() => {
        if (sampleJson) {
            try {
                const parsed = JSON.parse(sampleJson);
                setInferredPaths(flattenObject(Array.isArray(parsed) ? parsed[0] : parsed));
            } catch {
                setInferredPaths([]);
            }
        } else {
            setInferredPaths([]);
        }
    }, [sampleJson]);

    useEffect(() => {
        if (!businessId) return;
        axios.put(`${environment.baseUrl}/fetch/attribute`, {
            businessId, page: 0, size: 100, templateStatus: null, searchText: ''
        }).then(r => setAttributes(r.data.data.data || [])).catch(() => {});
    }, [businessId]);

    // Load once on open
    useEffect(() => {
        const node = nodes.find(n => n.data.id === data.nodeId);
        if (node) {
            const content = (node.data as CanvasNodeData).content.find(c => c.id === data.subNodeId);
            if (content?.data) {
                const d = content.data as any;
                setMode(d.mode || (d.arrayAttribute ? "dynamic" : "static"));
                setArrayAttribute(d.arrayAttribute || "");
                setTitlePath(d.titlePath || "");
                setDescPath(d.descPath || "");
                setImagePath(d.imagePath || "");
                setDynamicButtons(d.buttons || []);
                setStaticCards(d.cards || []);
                setBody(d.text || d.body || "Check out these options!");
                setSaveAs(d.saveAs || "");
                setSampleJson(d.sampleJson || "");
            }
        }
    }, []);

    const addStaticCard = () => {
        if (staticCards.length >= 10) return;
        setStaticCards([...staticCards, { id: crypto.randomUUID(), imageUrl: "", text: "Card " + (staticCards.length + 1), buttons: [] }]);
    };

    const updateStaticCard = (id: string, updates: Partial<StaticCard>) => {
        setStaticCards(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
    };

    const removeStaticCard = (id: string) => {
        setStaticCards(prev => prev.filter(c => c.id !== id));
    };

    const handleSave = () => {
        const updatedNodes = nodes.map(node => {
            if (node.data.id === data.nodeId) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        content: (node.data as CanvasNodeData).content.map(c => {
                            if (c.id === data.subNodeId) {
                                return {
                                    ...c,
                                    data: {
                                        ...c.data,
                                        mode,
                                        arrayAttribute: mode === "dynamic" ? arrayAttribute : null,
                                        titlePath: mode === "dynamic" ? titlePath : null,
                                        descPath: mode === "dynamic" ? descPath : null,
                                        imagePath: mode === "dynamic" ? imagePath : null,
                                        buttons: dynamicButtons,
                                        cards: mode === "static" ? staticCards : null,
                                        text: body,
                                        saveAs,
                                        sampleJson
                                    }
                                };
                            }
                            return c;
                        })
                    }
                };
            }
            return node;
        });
        setNodes(updatedNodes as CanvasNode[]);
        onClose();
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Mode toggle */}
            <div className="flex bg-slate-100 p-1 rounded-lg">
                <button
                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-medium rounded-md transition-all ${mode === 'dynamic' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                    onClick={() => setMode('dynamic')}
                >
                    <Database size={14} /> Dynamic
                </button>
                <button
                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-medium rounded-md transition-all ${mode === 'static' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                    onClick={() => setMode('static')}
                >
                    <Settings2 size={14} /> Static
                </button>
            </div>

            {/* Body text */}
            <div className="premium-field">
                <div className="flex justify-between items-center mb-1">
                    <label className="label-premium">Body Text (Main)</label>
                    <VariablePicker onSelect={val => setBody(prev => prev + `{{${val}}}`)} />
                </div>
                <textarea
                    className="premium-input"
                    style={{ minHeight: '60px' }}
                    placeholder="Briefly describe the carousel..."
                    value={body}
                    onChange={e => setBody(e.target.value)}
                />
            </div>

            {/* Save selection as */}
            <div className="premium-field">
                <label className="label-premium">Save selection as</label>
                <AttributePicker value={saveAs} onChange={setSaveAs} placeholder="— Save to attribute (optional) —" defaultType="OBJECT" />
                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>
                    Stores the chosen item under this attribute so later steps can use it via {'{{'}name{'}}'}. Leave blank to only use SELECTED_ITEM.
                </div>
            </div>

            {/* Dynamic mode fields */}
            {mode === 'dynamic' && <>
                <div className="premium-field">
                    <label className="label-premium">Carousel Data Source <span style={{ color: '#ef4444' }}>*</span></label>
                    <select
                        className={`premium-input ${!arrayAttribute ? 'border-amber-200 bg-amber-50/10' : ''}`}
                        value={arrayAttribute}
                        onChange={e => setArrayAttribute(e.target.value)}
                    >
                        <option value="">Select Array Attribute</option>
                        {attributes.filter(a => a.attributeType?.toUpperCase() === 'ARRAY').map(a => (
                            <option key={a.attributeId} value={a.attributeName}>{a.attributeName}</option>
                        ))}
                    </select>
                </div>

                <div style={{ borderTop: '1px solid #f1f5f9' }} />

                <div className="premium-field">
                    <label className="label-premium" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Info size={12} style={{ color: '#3b82f6' }} /> Card Data Mapping
                    </label>
                    <textarea
                        className="premium-input"
                        style={{ fontFamily: 'monospace', fontSize: '10px', background: '#f8fafc', borderStyle: 'dashed', marginTop: '6px' }}
                        placeholder='Sample item: { "name": "Card 1", "img": "http://..." }'
                        value={sampleJson}
                        onChange={e => setSampleJson(e.target.value)}
                    />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Title path */}
                    <div className="premium-field" style={{ position: 'relative' }}>
                        <label className="label-premium" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Type size={12} /> Title Path</label>
                        <div style={{ position: 'relative' }}>
                            <input className="premium-input" style={{ paddingRight: '28px' }} placeholder="e.g. name" value={titlePath} onChange={e => setTitlePath(e.target.value)} />
                            {inferredPaths.length > 0 && (
                                <button style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                                    onClick={() => setActiveDropdown(activeDropdown === 'title' ? null : 'title')}>
                                    <ChevronDown size={14} />
                                </button>
                            )}
                        </div>
                        {activeDropdown === 'title' && (
                            <div className="path-menu shadow-xl border border-slate-200">
                                {inferredPaths.map(p => <div key={p} className="path-menu-item" onClick={() => { setTitlePath(p); setActiveDropdown(null); }}>{p}</div>)}
                            </div>
                        )}
                    </div>

                    {/* Description path */}
                    <div className="premium-field" style={{ position: 'relative' }}>
                        <label className="label-premium" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FileText size={12} /> Description Path</label>
                        <div style={{ position: 'relative' }}>
                            <input className="premium-input" style={{ paddingRight: '28px' }} placeholder="e.g. description" value={descPath} onChange={e => setDescPath(e.target.value)} />
                            {inferredPaths.length > 0 && (
                                <button style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                                    onClick={() => setActiveDropdown(activeDropdown === 'desc' ? null : 'desc')}>
                                    <ChevronDown size={14} />
                                </button>
                            )}
                        </div>
                        {activeDropdown === 'desc' && (
                            <div className="path-menu shadow-xl border border-slate-200">
                                {inferredPaths.map(p => <div key={p} className="path-menu-item" onClick={() => { setDescPath(p); setActiveDropdown(null); }}>{p}</div>)}
                            </div>
                        )}
                    </div>

                    {/* Image path */}
                    <div className="premium-field" style={{ position: 'relative' }}>
                        <label className="label-premium" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><ImageIcon size={12} /> Image URL Path</label>
                        <div style={{ position: 'relative' }}>
                            <input className="premium-input" style={{ paddingRight: '28px' }} placeholder="e.g. imageUrl" value={imagePath} onChange={e => setImagePath(e.target.value)} />
                            {inferredPaths.length > 0 && (
                                <button style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                                    onClick={() => setActiveDropdown(activeDropdown === 'image' ? null : 'image')}>
                                    <ChevronDown size={14} />
                                </button>
                            )}
                        </div>
                        {activeDropdown === 'image' && (
                            <div className="path-menu shadow-xl border border-slate-200">
                                {inferredPaths.map(p => <div key={p} className="path-menu-item" onClick={() => { setImagePath(p); setActiveDropdown(null); }}>{p}</div>)}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', padding: '10px' }}>
                    <label className="label-premium" style={{ color: '#1e40af', marginBottom: '6px', display: 'block', fontStyle: 'italic' }}>Action Buttons (Dynamic)</label>
                    {dynamicButtons.map((btn, bi) => (
                        <div key={btn.id} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>
                            <input
                                className="premium-input"
                                placeholder={`Button ${bi + 1} label`}
                                maxLength={20}
                                value={btn.text}
                                onChange={e => setDynamicButtons(prev => prev.map((b, idx) => idx === bi ? { ...b, text: e.target.value } : b))}
                            />
                            <button style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}
                                onClick={() => setDynamicButtons(prev => prev.filter((_, idx) => idx !== bi))}>✕</button>
                        </div>
                    ))}
                    {dynamicButtons.length < 2 && (
                        <button style={{ fontSize: '11px', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }}
                            onClick={() => setDynamicButtons(prev => [...prev, { id: crypto.randomUUID(), text: '' }])}>
                            + Add button
                        </button>
                    )}
                </div>
            </>}

            {/* Static mode: card editor */}
            {mode === 'static' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '6px 8px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                        <label className="label-premium" style={{ fontWeight: 700 }}>Static Cards ({staticCards.length}/10)</label>
                        <button className="add-card-btn" style={{ borderColor: 'var(--primary-bg-color)', color: 'var(--primary-bg-color)' }} onClick={addStaticCard}>
                            <Plus size={14} /> Add Card
                        </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {staticCards.map((card) => (
                            <div key={card.id} className="card-container">
                                <button className="card-remove-btn" onClick={() => removeStaticCard(card.id)}>
                                    <Trash2 size={11} />
                                </button>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div className="premium-field">
                                        <label className="label-premium">Image URL <span style={{ color: '#ef4444' }}>*</span></label>
                                        <input
                                            className="premium-input"
                                            value={card.imageUrl}
                                            placeholder="https://..."
                                            onChange={e => updateStaticCard(card.id, { imageUrl: e.target.value })}
                                        />
                                    </div>
                                    <div className="premium-field">
                                        <label className="label-premium">Card Text</label>
                                        <textarea
                                            className="premium-input"
                                            style={{ minHeight: '40px' }}
                                            value={card.text}
                                            placeholder="Enter card details..."
                                            onChange={e => updateStaticCard(card.id, { text: e.target.value })}
                                        />
                                    </div>
                                    <div style={{ background: 'white', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                        <label className="label-premium" style={{ color: 'var(--primary-bg-color)', display: 'block', marginBottom: '6px' }}>
                                            Buttons (max 2) <span style={{ color: '#94a3b8', textTransform: 'none', fontWeight: 400, fontSize: '9px' }}>— each becomes a flow branch</span>
                                        </label>
                                        {(card.buttons || []).map((btn, bi) => (
                                            <div key={btn.id} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>
                                                <input
                                                    className="premium-input"
                                                    placeholder={`Button ${bi + 1} label`}
                                                    maxLength={20}
                                                    value={btn.text}
                                                    onChange={e => {
                                                        const newBtns = card.buttons.map((b, idx) => idx === bi ? { ...b, text: e.target.value } : b);
                                                        updateStaticCard(card.id, { buttons: newBtns });
                                                    }}
                                                />
                                                <button style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}
                                                    onClick={() => updateStaticCard(card.id, { buttons: card.buttons.filter((_, idx) => idx !== bi) })}>✕</button>
                                            </div>
                                        ))}
                                        {(card.buttons || []).length < 2 && (
                                            <button style={{ fontSize: '11px', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }}
                                                onClick={() => updateStaticCard(card.id, { buttons: [...(card.buttons || []), { id: crypto.randomUUID(), text: '' }] })}>
                                                + Add button
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {staticCards.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '24px', border: '1px dashed #e2e8f0', borderRadius: '8px', background: '#f8fafc' }}>
                            <ImageIcon size={20} style={{ margin: '0 auto 4px', color: '#cbd5e1' }} />
                            <p style={{ fontSize: '10px', color: '#94a3b8' }}>No cards added. Click "Add Card".</p>
                        </div>
                    )}
                </div>
            )}

            {/* Save / Cancel */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                <button
                    onClick={onClose}
                    style={{ padding: '8px 20px', fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', color: '#64748b' }}
                >Cancel</button>
                <button
                    onClick={handleSave}
                    style={{ padding: '8px 24px', fontSize: '13px', borderRadius: '8px', border: 'none', background: 'var(--primary-bg-color)', color: 'white', cursor: 'pointer', fontWeight: 600 }}
                >Save</button>
            </div>

            <style>{`
                .label-premium {
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                    color: #64748b;
                    font-weight: 700;
                    margin-bottom: 2px;
                }
                .premium-field {
                    display: flex;
                    flex-direction: column;
                }
                .premium-input {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 4px;
                    padding: 6px 8px;
                    font-size: 11px;
                    color: #1e293b;
                    width: 100%;
                }
                .premium-input:focus {
                    border-color: var(--primary-bg-color);
                    box-shadow: 0 0 0 2px rgba(10,71,76,0.1);
                    outline: none;
                }
                .add-card-btn {
                    background-color: #ffffff !important;
                    font-size: 10px !important;
                    padding: 4px 10px !important;
                    border-radius: 4px !important;
                    font-weight: 600 !important;
                    display: flex !important;
                    align-items: center !important;
                    gap: 4px !important;
                    border: 1px solid !important;
                    cursor: pointer !important;
                    width: auto !important;
                    height: auto !important;
                }
                .add-card-btn:hover { background: rgba(10,71,76,0.05) !important; }
                .card-container {
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 10px;
                    background: #f8fafc;
                    position: relative;
                }
                .card-remove-btn {
                    position: absolute !important;
                    top: 6px !important;
                    right: 6px !important;
                    background: #fee2e2 !important;
                    color: #ef4444 !important;
                    width: 22px !important;
                    height: 22px !important;
                    min-width: 22px !important;
                    border-radius: 50% !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    border: 1px solid #fecaca !important;
                    cursor: pointer !important;
                    padding: 0 !important;
                    margin: 0 !important;
                }
                .path-menu {
                    position: absolute; top: 100%; left: 0; right: 0;
                    background: white; border-radius: 6px; z-index: 100;
                    max-height: 120px; overflow-y: auto; margin-top: 2px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                .path-menu-item {
                    padding: 6px 10px; font-size: 10px; color: #475569;
                    cursor: pointer; border-bottom: 1px solid #f1f5f9;
                }
                .path-menu-item:hover { background: #f8fafc; color: #0ea5e9; }
            `}</style>
        </div>
    );
};

export default CarouselDialog;
